import { useEffect } from "react";

/**
 * Locks the page scroll behind a portaled overlay (modal, sheet, drawer).
 * Restores the previous `overflow` value on unlock / unmount.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   useBodyScrollLock(open);
 *
 * Compatible with multiple concurrent overlays — uses a refcount so locks
 * compose cleanly (the body only unlocks once every overlay has closed).
 */

let _lockCount = 0;
let _originalOverflow = "";
let _originalPaddingRight = "";

const applyLock = () => {
  _lockCount += 1;
  if (_lockCount === 1) {
    _originalOverflow = document.body.style.overflow;
    _originalPaddingRight = document.body.style.paddingRight;
    // Prevent layout shift when scrollbar disappears
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.style.overflow = "hidden";
  }
};

const releaseLock = () => {
  if (_lockCount === 0) return;
  _lockCount -= 1;
  if (_lockCount === 0) {
    document.body.style.overflow = _originalOverflow;
    document.body.style.paddingRight = _originalPaddingRight;
  }
};

export default function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    applyLock();
    return () => releaseLock();
  }, [active]);
}
